import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ManageSearch as ManageSearchIcon,
  CorporateFare as CorporateFareIcon,
} from "@mui/icons-material";
import { EmptyList, LoadingList } from "@/organism";

import { CompanyStampListItemDto } from "@/dto/CompanyStampDto";

/**
 * 도장 목록 프로퍼티
 */
export type ListProps = {
  /**
   * 도장 목록
   * @type {CompanyStampListItemDto[]}
   */
  list: CompanyStampListItemDto[];
  /**
   * 로딩 여부
   * @type {boolean}
   * @default false
   */
  isLoading?: boolean;
  /**
   * 상세 클릭 이벤트
   * @type {(comId: string) => void}
   */
  onDetail: (comId: string) => void;
};

/**
 * 도장 목록 컴포넌트
 *
 * @param {ListProps} props
 * @returns {JSX.Element}
 *
 * @author Max <sud665@ymjlab.com>
 * @since 2025-05-09
 * @version 1.0
 */
export const List = (props: ListProps) => {
  const { list, isLoading = false, onDetail } = props;

  /**
   * 구독 상태에 따른 Chip 컬러 반환
   */
  const getStatusColor = (status: string | null) => {
    if (!status) return "default";

    switch (status) {
      case "ACTIVE":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELED":
        return "error";
      default:
        return "default";
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingList />;
    }

    if (list.length === 0) {
      return <EmptyList />;
    }

    return list.map((company: CompanyStampListItemDto) => (
      <TableRow key={company.comId}>
        <TableCell>{company.comId}</TableCell>
        <TableCell>
          {company.branchYn === "Y" && company.parentsComNm ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" component="span">
                  {company.comNm}
                </Typography>
                <Chip
                  label="지점"
                  size="small"
                  color="info"
                  sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                />
              </Box>
              <Tooltip title="본점 정보" placement="right">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 0.5,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  <CorporateFareIcon
                    sx={{ fontSize: "1rem", mr: 0.5, opacity: 0.7 }}
                  />
                  <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                    본점: {company.parentsComNm}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          ) : (
            <Typography variant="body2">{company.comNm}</Typography>
          )}
        </TableCell>
        <TableCell>{company.admNm}</TableCell>
        <TableCell>{company.productNm || "-"}</TableCell>
        <TableCell>
          {company.subscriptionStat ? (
            <Chip
              label={company.subscriptionStat}
              color={getStatusColor(company.subscriptionStat)}
              size="small"
            />
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell>{company.templateNm || "-"}</TableCell>
        <TableCell>
          {new Date(company.mapRegDttm).toLocaleDateString()}
        </TableCell>
        <TableCell>
          <IconButton color={"primary"} onClick={() => onDetail(company.comId)}>
            <ManageSearchIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Paper variant={"outlined"}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>도장명</TableCell>
              <TableCell>관리자명</TableCell>
              <TableCell>상품</TableCell>
              <TableCell>구독상태</TableCell>
              <TableCell>템플릿</TableCell>
              <TableCell>등록일</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderContent()}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
